import { useEffect, useState } from "react";
import { X } from "lucide-react";
import WorkflowService from "../../../services/workflow.service";
import { formatDuration, formatTime } from "../executionTimeline";

// El detalle de una entrada. El listado recorta lo pesado —los parámetros
// resueltos de un ai_agent llevan su prompt entero—, así que al abrir una
// entrada se pide la versión completa a `/timeline/{seq}`. Si esa llamada
// falla, se muestra lo que ya se tenía en vez de dejar el panel vacío.

const Field = ({ label, children }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wider text-gray-500">{label}</div>
    <div className="mt-1 text-xs text-gray-200">{children ?? "—"}</div>
  </div>
);

const Json = ({ label, value, truncated }) => {
  if (value === null || value === undefined) return null;
  const isEmpty = typeof value === "object" && Object.keys(value).length === 0;
  if (isEmpty) return null;

  return (
    <div>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-wider text-gray-500">{label}</div>
        {truncated && <span className="text-[9px] text-amber-400/80">trimmed</span>}
      </div>
      <pre className="mt-1 max-h-64 overflow-auto rounded-lg border border-white/10 bg-black/40 p-2 text-[10px] leading-relaxed text-gray-300">
        {JSON.stringify(value, null, 2)}
      </pre>
    </div>
  );
};

export default function EntryDetailPanel({ runId, projectId, entry, onClose }) {
  const [full, setFull] = useState(null);
  const [loading, setLoading] = useState(false);

  const seq = entry?.seq;
  useEffect(() => {
    if (!runId || !projectId || !seq) return undefined;
    let cancelled = false;

    setLoading(true);
    setFull(null);
    WorkflowService.getTimelineEntry(runId, seq, { project_id: projectId }).then((result) => {
      if (cancelled) return;
      if (result.success) setFull(result.data.entry);
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [runId, projectId, seq]);

  if (!entry) {
    return (
      <div className="flex h-full items-center justify-center px-6 text-center text-[11px] text-gray-600">
        Pick an entry to see what it ran with and what it returned.
      </div>
    );
  }

  // Mientras llega la versión completa se muestra la recortada: el nombre, la
  // duración y el estado ya son correctos en ella.
  const shown = full || entry;
  const isWait = shown.kind === "wait";

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-start justify-between border-b border-white/10 px-4 py-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span
              className={`rounded px-1.5 py-0.5 text-[9px] font-semibold uppercase ${
                isWait ? "bg-blue-500/15 text-blue-300" : "bg-emerald-500/15 text-emerald-400"
              }`}
            >
              {shown.kind}
            </span>
            <span className="font-mono text-[10px] text-gray-600">seq {shown.seq}</span>
            {loading && (
              <span className="h-3 w-3 animate-spin rounded-full border border-violet-500 border-t-transparent" />
            )}
          </div>
          <h4 className="mt-1.5 truncate text-sm font-medium text-white">{shown.name || "—"}</h4>
          <p className="truncate text-[10px] text-gray-500">{shown.type || "—"}</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded p-1 text-gray-500 transition-colors hover:bg-white/5 hover:text-white"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="flex-1 space-y-4 overflow-auto px-4 py-4">
        <div className="grid grid-cols-2 gap-4">
          <Field label="Status">{shown.status}</Field>
          <Field label="Duration">
            {shown.duration_ms === null || shown.duration_ms === undefined
              ? "still open"
              : formatDuration(shown.duration_ms)}
          </Field>
          <Field label="Started">{formatTime(shown.started_at)}</Field>
          <Field label="Ended">{shown.completed_at ? formatTime(shown.completed_at) : "—"}</Field>
        </div>

        {isWait ? (
          <div className="grid grid-cols-2 gap-4">
            <Field label="Reason">{shown.reason}</Field>
            <Field label="Released by">{shown.resumed_by?.detail || "—"}</Field>
            <Field label="Timeout">
              {shown.timeout_s ? formatDuration(shown.timeout_s * 1000) : "—"}
            </Field>
            <Field label="Expires">{shown.expires_at ? formatTime(shown.expires_at) : "—"}</Field>
            <div className="col-span-2">
              <Json label="Match" value={shown.match} />
            </div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Pass">{shown.pass_number ?? "—"}</Field>
              <Field label="Retries">{shown.retries ?? "—"}</Field>
              <Field label="Cost">
                {shown.cost
                  ? `${shown.cost.coins} ${shown.cost.coins === 1 ? "coin" : "coins"}`
                  : "—"}
              </Field>
              {/* `null` no es cero: es que nadie midió el costo de este paso. */}
              <Field label="Cost source">{shown.cost?.source || "not measured"}</Field>
            </div>

            {shown.error && (
              <div className="rounded-lg border border-rose-500/30 bg-rose-500/5 p-2.5 text-[11px] text-rose-200">
                {shown.error.message || String(shown.error)}
              </div>
            )}

            <Json label="Input" value={shown.input} truncated={shown.input_truncated} />
            <Json label="Output" value={shown.output} truncated={shown.output_truncated} />
            <Json label="Output variables" value={shown.output_variables} />
          </>
        )}
      </div>
    </div>
  );
}
